import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Download, FileText } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";

export const Route = createFileRoute("/_app/invoices")({
  component: InvoicesPage,
});

interface InvoiceRow {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  kind: string;
  period: string | null;
  notes: string | null;
  companyName: string | null;
  createdAt: number;
}

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function InvoicesPage() {
  const [year, setYear] = useState<string>(() => String(new Date().getFullYear()));
  const { data, isLoading } = useQuery<{ documents: InvoiceRow[] }>({
    queryKey: ["invoices", year],
    queryFn: () => api.get(`/invoices?year=${year}`),
  });
  const docs = data?.documents ?? [];
  const byPeriod = new Map<string, InvoiceRow[]>();
  for (const d of docs) {
    const key = d.period ?? "(sin período)";
    const arr = byPeriod.get(key) ?? [];
    arr.push(d);
    byPeriod.set(key, arr);
  }
  const sortedPeriods = [...byPeriod.keys()].sort().reverse();

  const years = (() => {
    const now = new Date().getFullYear();
    return [now, now - 1, now - 2, now - 3];
  })();

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Facturas"
        title="Tus facturas con Axorial"
        subtitle="CFDIs y comprobantes marcados como visibles por el equipo. Si esperabas ver alguno y no aparece, pídeselo a tu lead en Axorial."
        actions={
          <select
            className="select select-bordered select-sm"
            value={year}
            onChange={(e) => setYear(e.target.value)}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        }
      />

      {isLoading ? (
        <div className="grid place-items-center py-16">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      ) : docs.length === 0 ? (
        <div className="card-elevated p-10 text-center text-sm text-base-content/55">
          Sin facturas visibles para {year}.
        </div>
      ) : (
        <div className="space-y-4">
          {sortedPeriods.map((period) => (
            <section key={period} className="card-elevated overflow-hidden">
              <header className="px-4 py-3 border-b border-base-300/40 flex items-baseline justify-between">
                <h3 className="font-display font-semibold text-sm">
                  {formatPeriod(period)}
                </h3>
                <span className="text-xs text-base-content/55">
                  {byPeriod.get(period)!.length}{" "}
                  {byPeriod.get(period)!.length === 1 ? "factura" : "facturas"}
                </span>
              </header>
              <ul className="divide-y divide-base-300/40">
                {byPeriod.get(period)!.map((d) => (
                  <li key={d.id} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                    <FileText className="size-5 text-base-content/55 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">{d.filename}</div>
                      <div className="text-xs text-base-content/55 flex items-center gap-2 flex-wrap">
                        <span className="badge badge-xs badge-ghost">{d.kind}</span>
                        <span>{fmtSize(d.sizeBytes)}</span>
                        {d.companyName && <span>· {d.companyName}</span>}
                      </div>
                      {d.notes && (
                        <div className="text-xs text-base-content/65 mt-0.5 line-clamp-2">
                          {d.notes}
                        </div>
                      )}
                    </div>
                    <a
                      href={`/api/invoices/${d.id}/download`}
                      download={d.filename}
                      className="btn btn-ghost btn-sm gap-1 shrink-0"
                    >
                      <Download className="size-4" /> Descargar
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function formatPeriod(p: string): string {
  if (!/^\d{4}-\d{2}$/.test(p)) return p;
  const [y, m] = p.split("-");
  const months = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];
  return `${months[parseInt(m!, 10) - 1] ?? m} ${y}`;
}
